import {
  AddSubstateData,
  ComponentMoveData,
  ComponentOverdriveData,
  DiscardComponentData,
  GameComponentForGame,
  GameMoveForEval,
  GeneralComponentForGame,
  IncStatData,
  MoveAllData,
  MoveToBankData,
  PushStatData,
  ReshuffleData,
  RevealAllData,
  RevealComponentData,
  RevealToDeckData,
  SelectComponentData,
  SetCounterData,
  SetGameStateData,
  SetGameStateVariable,
  SetGlobalData,
  SetObjectStatData,
  SetPlayerAttrData,
  SetVariableData,
} from "../implementationTypes";
import { GameComponent, GameSituationBackend, StatValue } from "../types";
import { manipulationMethods } from "./StateOfGameManipulation";
import { audio, mergeDeep } from "./tableUtility";

export function getAdjustFunction(
  moveData: GameMoveForEval,
  gameSituation: GameSituationBackend,
  realTime: boolean,
  playSound: boolean,
  playAsColor: string,
  animationData?: any,
): (draft: GameSituationBackend | undefined) => any {
  let emptyFunction = (draft: GameSituationBackend | undefined) => undefined;
  switch (moveData.type) {
    case "inc counter":
    case "set counter":
      return (draft) => {
        let gameMoveData = moveData as SetCounterData;
        let id = "player-counter-display-" + gameMoveData.color + "-" + gameMoveData.name;

        let display = draft?.getGameComponentById(id);

        if (draft === undefined) {
          return;
        }

        let newValue = +gameMoveData.value;
        for (let p of draft.players) {
          if (p.color === gameMoveData.color) {
            for (let c of p.counters) {
              if (c.name === gameMoveData.name) {
                if (gameMoveData.type === "inc counter") {
                  if (typeof c.value === "number" && typeof gameMoveData.value === "number") {
                    newValue = c.value + gameMoveData.value;
                  }
                } else {
                  newValue = +gameMoveData.value;
                }
                if (moveData.setMaxValue) {
                  c.options.maxValue = newValue;
                } else {
                  c.value = newValue;
                }
                if (display) {
                  display.value = newValue;
                }
                draft.forceRecalculateIds = true;
                return;
              }
            }
          }
        }
      };

      break;
    case "inc stat":
      return (draft) => {
        let gameMoveData = moveData as IncStatData;
        if (!draft || !gameMoveData.statName || gameMoveData.statValue === undefined) {
          return;
        }
        let stats: { [key: string]: StatValue } | undefined = draft.stats ?? {};

        if (gameMoveData.player && gameMoveData.player !== "alwaysactive") {
          stats = draft.players.find((p) => p.color === gameMoveData.player)?.stats ?? {};
          if (!stats) {
            throw new Error("Can't find stats for player " + gameMoveData.player);
          }
        }
        if (stats === undefined) {
          stats = {};
        }
        if (!stats[gameMoveData.statName]) {
          stats[gameMoveData.statName] = gameMoveData.statValue;
        } else {
          if (gameMoveData.absolute) {
            if (gameMoveData.compare && gameMoveData.statValue <= stats[gameMoveData.statName]) {
              return;
            }
            stats[gameMoveData.statName] = gameMoveData.statValue;
          } else if (typeof stats[gameMoveData.statName] === "string" && typeof gameMoveData.statValue === "string") {
            stats[gameMoveData.statName] += gameMoveData.statValue;
          } else {
            stats[gameMoveData.statName] =
              (stats[gameMoveData.statName] as number) + (gameMoveData.statValue as number);
          }
        }
      };
      break;
    case "push stat":
      return (draft) => {
        let gameMoveData = moveData as PushStatData;
        if (!draft || gameMoveData.statName == undefined || gameMoveData.statValue === undefined) {
          return;
        }
        let stats: { [key: string]: StatValue } = draft.stats;

        if (gameMoveData.player) {
          stats = draft.players.find((p) => p.color === gameMoveData.player)?.stats as { [key: string]: StatValue };
          if (!stats) {
            throw new Error("Can't find stats for player " + gameMoveData.player);
          }
        }
        if (stats[gameMoveData.statName] && Array.isArray(stats[gameMoveData.statName])) {
          (stats[gameMoveData.statName] as any[]).push(gameMoveData.statValue);
        } else {
          stats[gameMoveData.statName] = [gameMoveData.statValue as number | string];
        }
      };
      break;
    case "set object stat":
      return (draft) => {
        let gameMoveData = moveData as SetObjectStatData;
        if (!draft || !gameMoveData.objectName || !gameMoveData.fieldName || gameMoveData.statValue === undefined) {
          return draft;
        }
        let stats = draft.objectStats;
        if (!stats[gameMoveData.objectName]) {
          stats[gameMoveData.objectName] = {};
        }
        if (
          gameMoveData.absolute ||
          !stats[gameMoveData.objectName] ||
          !stats[gameMoveData.objectName][gameMoveData.fieldName]
        ) {
          stats[gameMoveData.objectName][gameMoveData.fieldName] = gameMoveData.statValue;
        } else {
          (stats[gameMoveData.objectName][gameMoveData.fieldName] as number) += gameMoveData.statValue as number;
        }
      };
      break;
    case "component move":
      return (draft) => {
        let gameMoveData = moveData as ComponentMoveData;

        if (!draft || gameMoveData.componentId === undefined || gameMoveData.positionIndex === undefined) {
          return draft;
        }
        gameSituation.forceRecalculateIds = true;
        let movingComponent = draft.getGameComponentById(gameMoveData.componentId);
        if (!movingComponent) {
          return;
        }
        let parentId = draft.getGameComponentById(gameMoveData.componentId)?.parent;
        let movingParentComponent = draft.getGameComponentById(parentId ?? "");
        if (gameMoveData.destinationId == undefined) {
          gameMoveData.destinationId = parentId;
        }
        if (!movingParentComponent) {
          return;
        }
        let destinationComponent = draft.getGameComponentById(gameMoveData.destinationId ?? "");
        if (destinationComponent == undefined) {
          return;
        }
        let positionName = gameMoveData.positionName ?? movingComponent.childData?.positionName ?? "unpositioned";
        manipulationMethods.moveComponent(
          movingComponent,
          movingParentComponent,
          destinationComponent,
          positionName,
          gameMoveData.positionIndex,
          (gameMoveData.positionStart ? 0 : gameMoveData.multiPositionIndex) ?? undefined,
          realTime,
          animationData,
        );
        try {
          if (playSound) {
            if (movingComponent?.name?.includes("card")) {
              audio.card.play();
            } else {
              audio.wood.play();
            }
          }
        } catch (e) {
          console.log("Error playing sound", e);
        }
        draft.forceRecalculateIds = true;
      };
      break;
    case "move all":
      return (draft) => {
        let gameMoveData = moveData as MoveAllData;
        if (!gameMoveData.destinationId) {
          gameMoveData.destinationId = gameMoveData.originId;
        }
        if (!draft || gameMoveData.originId === undefined) {
          return draft;
        }
        draft.forceRecalculateIds = true;

        let originComponent = draft.getGameComponentById(gameMoveData.originId);
        if (!originComponent) {
          return;
        }
        let destinationComponent = draft.getGameComponentById(gameMoveData.destinationId);

        manipulationMethods.moveAll(
          originComponent,
          gameMoveData.originPositionName ?? "unpositioned",
          gameMoveData.originPositionIndex ?? "0",
          destinationComponent ?? originComponent,
          gameMoveData.destinationPositionName ?? "unpositioned",
          gameMoveData.destinationPositionIndex ?? "0",
          realTime,
        );
        draft.forceRecalculateIds = true;
      };
      break;
    case "reveal all":
      return (draft) => {
        let gameMoveData = moveData as RevealAllData;
        if (!draft) {
          return draft;
        }
        draft.forceRecalculateIds = true;

        let components = gameMoveData.revealedComponents || [];
        if (gameMoveData.containerId && draft.containers[gameMoveData.containerId]) {
          draft.containers[gameMoveData.containerId].componentAmt = 0;
        }
        if (moveData.discardSize !== undefined) {
          draft.containers[gameMoveData.containerId].discardSize = 0;
        }

        for (let component of components) {
          if (component?.name !== "unknown" && gameMoveData.adjustOptions !== undefined) {
            for (let optionName in gameMoveData.adjustOptions ?? {}) {
              manipulationMethods.setComponentOption(component, optionName, gameMoveData.adjustOptions[optionName]);
            }
          }
          manipulationMethods.placeComponent(
            component,
            draft.getGameComponentById(gameMoveData.destinationId ?? "") as GameComponent,
            gameMoveData.positionName,
            gameMoveData.positionIndex,
            gameMoveData.multiPositionIndex,
            animationData,
          );
        }
        if (moveData.deckSize) {
          draft.containers[gameMoveData.containerId].componentAmt = moveData.deckSize;
        }
        draft.forceRecalculateIds = true;
      };
      break;
    case "component reveal":
    case "component create":
      return (draft) => {
        let gameMoveData = moveData as RevealComponentData;
        if (!draft || moveData.revealedComponent === undefined) {
          return;
        }

        let revComponent = moveData.revealedComponent;
        if (moveData.type === "component create") {
          if (!revComponent.id && draft.createCounter) {
            if (!draft.createCounter[revComponent.name]) {
              draft.createCounter[revComponent.name] = 0;
            }
            draft.createCounter[revComponent.name] += 1;
            let id = "__created_" + revComponent.name + "#" + draft.createCounter[revComponent.name];
            revComponent.id = id;
          }
          if (!revComponent.options) {
            revComponent.options = {};
          }
          if (!revComponent.children) {
            revComponent.children = {};
          }
        }
        let component = revComponent as GameComponentForGame;
        if (gameMoveData.containerId && draft.containers[gameMoveData.containerId]) {
          draft.containers[gameMoveData.containerId].componentAmt =
            moveData.deckSize ?? draft.containers[gameMoveData.containerId].componentAmt - 1 ?? 0;
        }
        if (moveData.discardSize !== undefined && gameMoveData.containerId) {
          draft.containers[gameMoveData.containerId].discardSize = moveData.discardSize;
        }
        if (moveData.deckTopCard !== undefined && gameMoveData.containerId) {
          draft.containers[gameMoveData.containerId].deckTopCard = moveData.deckTopCard;
        }
        if (gameMoveData.containerId && moveData.deckSize === 0) {
          draft.containers[gameMoveData.containerId].deckTopCard = null;
        }

        if (component !== undefined && component?.name !== "unknown" && gameMoveData.adjustOptions) {
          for (let optionName in gameMoveData.adjustOptions) {
            manipulationMethods.setComponentOption(component, optionName, gameMoveData.adjustOptions[optionName]);
          }
        }
        let destinationComponent = draft.getGameComponentById(gameMoveData.destinationId ?? "");
        if (!destinationComponent) {
          return;
        }
        let aData = animationData;
        if (aData) {
          aData.flipped = true;
        }

        if (playSound) {
          if (component?.name?.includes("card")) {
            audio.card.play();
          } else {
            audio.wood.play();
          }
        }

        manipulationMethods.placeComponent(
          component,
          destinationComponent,
          gameMoveData.positionName,
          gameMoveData.positionIndex,
          gameMoveData.positionStart ? 0 : gameMoveData.multiPositionIndex,
          realTime ? aData : undefined,
        );
        draft.forceRecalculateIds = true;
      };
      break;
    case "reveal to deck": {
      return (draft) => {
        let gameMoveData = moveData as RevealToDeckData;
        if (!draft) {
          return;
        }
        if (gameMoveData.containerId && draft.containers[gameMoveData.containerId]) {
          draft.containers[gameMoveData.containerId].componentAmt =
            moveData.deckSize ?? draft.containers[gameMoveData.containerId].componentAmt - 1 ?? 0;
        }
        if (moveData.discardSize !== undefined && gameMoveData.containerId) {
          draft.containers[gameMoveData.containerId].discardSize = moveData.discardSize;
        }
        if (moveData.deckTopCard !== undefined && gameMoveData.containerId) {
          draft.containers[gameMoveData.containerId].deckTopCard = moveData.deckTopCard;
        }
        if (gameMoveData.containerId && moveData.deckSize === 0) {
          draft.containers[gameMoveData.containerId].deckTopCard = null;
        }
        if (gameMoveData.destination && draft.containers[gameMoveData.destination]) {
          draft.containers[gameMoveData.destination].componentAmt =
            moveData.destDeckSize ?? draft.containers[gameMoveData.destination].componentAmt + 1;
        }
        if (moveData.destDeckTopCard !== undefined && gameMoveData.destination) {
          draft.containers[gameMoveData.destination].deckTopCard = moveData.destDeckTopCard;
        }
        draft.forceRecalculateIds = true;
      };
      break;
    }
    case "move to bank": {
      let gameMoveData = moveData as MoveToBankData;
      if (gameMoveData.componentId === undefined) {
        break;
      }
      return (draft) => {
        let movingComponent = gameSituation?.getGameComponentById(gameMoveData.componentId);
        if (movingComponent) {
          let parentComponent = gameSituation?.getGameComponentById(movingComponent.parent) as GameComponent;
          manipulationMethods.removeComponent(parentComponent, movingComponent.id);
        }
        if (!draft || gameMoveData.containerId === undefined) {
          return;
        }
        draft.forceRecalculateIds = true;
        draft.containers[gameMoveData.containerId].componentAmt += 1;
      };
      break;
    }
    case "discard":
    case "returnToTop":
    case "return to top":
    case "put to bottom": {
      return (draft) => {
        let gameMoveData = moveData as DiscardComponentData;
        let movingComponent =
          gameSituation?.getGameComponentById(gameMoveData.componentId ?? gameMoveData.componentData?.id) ??
          (gameMoveData.componentData as GeneralComponentForGame);
        if (!movingComponent) {
          debugger;
        }
        if (!gameMoveData.destination) {
          gameMoveData.destination = (movingComponent as GameComponent)?.origin ?? "void";
        }
        if (!draft || gameMoveData.destination === undefined) {
          return;
        }
        if (gameMoveData.destination && draft.containers[gameMoveData.destination]) {
          if (gameMoveData.type === "discard") {
            draft.containers[gameMoveData.destination].discardSize =
              moveData.deckSize ?? draft.containers[gameMoveData.destination].discardSize - 1 ?? 0;
          } else {
            draft.containers[gameMoveData.destination].componentAmt =
              moveData.deckSize ?? draft.containers[gameMoveData.destination].componentAmt - 1 ?? 0;
          }
        }
        if (moveData.discardSize !== undefined && gameMoveData.destination) {
          draft.containers[gameMoveData.destination].discardSize = moveData.discardSize;
        }
        if (moveData.deckTopCard !== undefined && gameMoveData.destination) {
          draft.containers[gameMoveData.destination].deckTopCard = moveData.deckTopCard;
        }
        if (gameMoveData.destination && moveData.deckSize === 0) {
          draft.containers[gameMoveData.destination].deckTopCard = null;
        }
        draft.forceRecalculateIds = true; // I don't know why we need to trigger recalc of component map here. TODO: find out
        if (!movingComponent.parent) {
          let gameComponent = gameSituation?.getGameComponentById(movingComponent.id);
          movingComponent.parent = gameComponent?.parent;
        }
        let parentComponent = gameSituation?.getGameComponentById(movingComponent.parent) as GameComponent;
        manipulationMethods.removeComponent(parentComponent, movingComponent.id);
        draft.forceRecalculateIds = true;
        if (playSound) {
          audio.card.play();
        }
        let targetDeck =
          draft.containers[gameMoveData.destinationId ?? -1] ?? draft.containers[gameMoveData.destination];
        if (moveData.deckSize !== undefined) {
          if (!targetDeck) {
            return;
          }
          targetDeck.componentAmt = moveData.deckSize ?? targetDeck.componentAmt + 1;
        }
        if (moveData.discardSize !== undefined) {
          targetDeck.discardSize = moveData.discardSize;
        }
        if (!moveData.componentData) {
          moveData.componentData = draft.getGameComponentById(moveData.componentId);
        }
        if (moveData.componentData) {
          moveData.componentData.parent = undefined;
        }
      };
      break;
    }
    case "reshuffle": {
      return (draft) => {
        let gameMoveData = moveData as ReshuffleData;
        if (!draft || gameMoveData.containerId === undefined) {
          return;
        }
        let targetDeck = draft.containers[gameMoveData.containerId ?? -1];
        if (moveData.deckSize !== undefined) {
          if (!targetDeck) {
            return;
          }
          targetDeck.componentAmt = moveData.deckSize ?? targetDeck.componentAmt + 1;
        }
        if (moveData.discardSize !== undefined) {
          targetDeck.discardSize = moveData.discardSize;
        }
        if (gameMoveData.containerId && draft.containers[gameMoveData.containerId]) {
          draft.containers[gameMoveData.containerId].componentAmt =
            moveData.deckSize ?? draft.containers[gameMoveData.containerId].componentAmt - 1 ?? 0;
        }
        if (moveData.deckTopCard !== undefined && gameMoveData.containerId) {
          draft.containers[gameMoveData.containerId].deckTopCard = moveData.deckTopCard;
        }
      };
    }
    case "set component option":
    case "set variable":
    case "randomize variable": {
      let gameMoveData = moveData as SetVariableData;
      return (draft) => {
        if (draft === undefined || gameMoveData.componentId === undefined || gameMoveData.newValue === undefined) {
          return;
        }

        let component = gameMoveData.componentId;
        if (!component) {
          return;
        }
        draft.forceRecalculateIds = true;
        manipulationMethods.setComponentOption(
          draft.getGameComponentById(component) as GameComponent,
          gameMoveData.optionName ?? (gameMoveData as unknown as { varName: string }).varName, // this is for backwards comp with old replays
          gameMoveData.newValue,
        );
        draft.forceRecalculateIds = true;
      };
      break;
    }
    case "set gamestate":
      return (draft) => {
        let gameMoveData = moveData as SetGameStateData;
        if (!draft || gameMoveData.newState === undefined) {
          return;
        }
        let prevActive = draft.getActiveState().activePlayer;
        let newActive = gameMoveData.newState.activePlayer;
        if (prevActive !== newActive && newActive === playAsColor) {
          let prevActive = draft.getActiveState().activePlayer;
          let newActive = gameMoveData.newState.activePlayer;
        }
        let newState = JSON.parse(JSON.stringify(gameMoveData.newState));
        if (typeof newState.stateId !== "string") {
          newState.stateId = newState.name + Date.now().toString(36);
        }
        if (typeof newState.stateVersion !== "number") {
          newState.stateVersion = 0;
        }

        draft.setGameState(newState);
        draft.forceRecalculateIds = true;
      };
      break;
    case "add substate":
      return (draft) => {
        let gameMoveData = moveData as AddSubstateData;
        if (!draft) {
          return;
        }
        let newState = JSON.parse(JSON.stringify(gameMoveData.newState));
        draft.addSubstate(newState);
      };
      break;
    case "resolve curr state":
      return (draft) => {
        if (!draft) {
          return;
        }
        draft.resolveCurrState();
      };
      break;
    case "set global":
      return (draft) => {
        let gameMoveData = moveData as SetGlobalData;
        if (!draft || gameMoveData.variableName === undefined || gameMoveData.newValue === undefined) {
          return;
        }
        draft.setGlobal(gameMoveData.variableName, gameMoveData.newValue);
        let id = "variable-display-" + gameMoveData.variableName;
        draft.forceRecalculateIds = true;
        let display = draft.getGameComponentById(id);
        if (display) {
          display.value = gameMoveData.newValue as string | number;
        }
      };
      break;
    case "set gamestate variable":
      return (draft) => {
        let gameMoveData = moveData as SetGameStateVariable;
        if (!draft || gameMoveData.variableName === undefined) {
          return;
        }
        let state = JSON.parse(JSON.stringify(draft.gameState.current));
        let currState = state;
        let lastCorrect;
        while (currState) {
          if (currState.name === gameMoveData.targetStateName || gameMoveData.targetStateName === undefined) {
            lastCorrect = currState;
          }
          currState = currState.subState;
        }
        if (!lastCorrect) {
          //no active state or targetStateName not in the current state chain
          console.error("set gamestate variable: target state not found", gameMoveData);
          return;
        }
        lastCorrect[gameMoveData.variableName] = gameMoveData.newValue;
        if (typeof lastCorrect.stateVersion !== "number") {
          lastCorrect.stateVersion = 1;
        } else {
          lastCorrect.stateVersion++;
        }
        draft.gameState.current = state;
        //draft.resolveCurrState();
        //state[gameMoveData.variableName] = gameMoveData.newValue;
        //draft.addSubstate(state)
      };
      break;
    case "set component overdrive":
      return (draft) => {
        let gameMoveData = moveData as ComponentOverdriveData;
        if (!draft || gameMoveData.overdrive === undefined) {
          return;
        }
        if (!draft.componentOverdrive) {
          draft.componentOverdrive = {};
        }
        mergeDeep(draft.componentOverdrive, gameMoveData.overdrive);
      };
      break;
    case "set player attr":
      return (draft) => {
        let gameMoveData = moveData as SetPlayerAttrData;
        if (!draft || gameMoveData.attrName === undefined) {
          return;
        }
        for (let p in draft.players) {
          if (draft.players[p].color === gameMoveData.color) {
            let newPlayer = JSON.parse(JSON.stringify(draft.players[p]));
            newPlayer.customAttrs[gameMoveData.attrName] = gameMoveData.newValue;
            draft.players[p] = newPlayer;
            break;
          }
        }
      };
      break;

    case "select component":
      return (draft) => {
        if (!draft) {
          return;
        }
        draft.forceSelectedComponent = (moveData as SelectComponentData).componentToSelect;
      };
      break;
    default:
      return emptyFunction;
  }
  return emptyFunction;
}
